import { useState } from 'react';
import { InfiniteList } from '@src/design-system/molecules/infinite-list';
import { Bookmark2, Edit2, Lock1 } from 'iconsax-react';
import { cn } from '@src/design-system/helpers';

import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';
import { Button } from '@src/design-system/button';

import type { TView } from '@shared/types/view';
import viewApi from '../services/view-api';
import { EarthIcon, EllipsisVerticalIcon, Trash2 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@src/design-system/tooltip';
import { MarkViewAsDefaultBtn } from './mark-view-as-default-btn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@src/design-system/dropdown-menu';
import { Dialog, DialogContent } from '@src/design-system/dialog';
import { DeleteConfirmDialog } from '@src/design-system/molecules/delete-confirm-dialog-button';
import { useTableStates } from '@design-system/molecules/ticlick-table/hooks/use-table-states';
import { useEditViewDialog } from './edit-view-dialog-host';

export function ViewsPopoverContent({
  hideEdit = false,
}: {
  /** Avoid nested edit dialogs when this list is inside EditViewDialog. */
  hideEdit?: boolean;
}) {
  const { table } = useTiclickTableContext();

  const { viewEntityType, viewEntityId } =
    table.options.meta?.identifyingData || {};

  return (
    <InfiniteList
      fetchInfiniteData={viewApi.useFetchInfinite}
      query={{ entity_type: viewEntityType, entity_id: viewEntityId }}
      enableSearch
      props={{
        listContainerProps: { className: 'h-[300px] p-4' },
        searchContainerProps: {
          className: 'mb-0',
        },
        listProps: {
          className: 'w-full gap-2',
        },
        infiniteScrollProps: {
          threshold: 0,
        },
      }}
      renderItem={(view) => {
        return (
          <ViewItem
            key={view.id}
            view={view}
            hideEdit={hideEdit}
          />
        );
      }}
    />
  );
}

const ViewItem = ({
  view,
  hideEdit,
}: {
  view: TView;
  hideEdit?: boolean;
}) => {
  const { name, is_default } = view;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { setCurrentView, currentView } = useTableStates();
  const { openEditView } = useEditViewDialog();

  const { mutate: deleteView, isPending } = viewApi.useDelete();

  const isSelected = currentView?.id === view.id;

  return (
    <Dialog
      open={isDeleteDialogOpen}
      onOpenChange={setIsDeleteDialogOpen}
    >
      <div
        className={cn(
          'group/view-item flex-1 min-w-0 rounded-xl flex justify-between items-center border-pale-border border p-1 pe-2',
          isSelected && 'border-active-border bg-primary-30'
        )}
      >
        <button
          onClick={() => {
            setTimeout(() => setCurrentView(view), 0);
          }}
          className='flex min-w-0 flex-1 items-center cursor-pointer text-left bg-transparent border-0 p-0 outline-none'
        >
          <div className='flex min-w-0 items-center'>
            <div className='size-8 shrink-0 me-1 min-w-0 bg-primary-50 rounded-lg flex items-center justify-center text-primary-1000'>
              <Bookmark2
                variant='Bulk'
                size={16}
              />
            </div>
            <span
              title={name}
              className='text-sm min-w-0 font-medium truncate whitespace-nowrap'
            >
              {name}
            </span>
          </div>
        </button>

        <div className='flex justify-center items-center gap-1'>
          {!hideEdit && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                openEditView(view);
              }}
              size='iconXs'
              variant='ghost'
              className='invisible size-6 text-primary-1000 hover:bg-primary-50 shrink-0 group-hover/view-item:visible'
            >
              <Edit2
                size={16}
                variant='Bulk'
              />
            </Button>
          )}

          {view.is_public ? (
            <span className='flex size-6 justify-center rounded-lg items-center gap-1 text-blue-600 bg-blue-50'>
              <EarthIcon size={16} />
            </span>
          ) : (
            <span className='flex size-6 justify-center rounded-lg items-center gap-1 text-gray-900 bg-primary-50'>
              <Lock1
                size={16}
                variant='Bulk'
              />
            </span>
          )}

          <Tooltip>
            <TooltipTrigger>
              <MarkViewAsDefaultBtn
                isDefault={is_default}
                staticFilterId={view?.id}
              />
            </TooltipTrigger>
            {is_default && <TooltipContent>Default view</TooltipContent>}
          </Tooltip>

          {!isSelected && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  size='iconXs'
                  variant='ghost'
                  className='size-6 text-gray-800 bg-primary-50 hover:bg-primary-100 shrink-0'
                >
                  <EllipsisVerticalIcon size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setIsDeleteDialogOpen(true);
                  }}
                  className='text-xs gap-1 font-medium text-red-500 cursor-pointer'
                >
                  <Trash2 size={16} />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <DialogContent align='center'>
        <DeleteConfirmDialog
          title={`Are you sure you want to delete this view?`}
          isLoading={isPending}
          onConfirm={() =>
            deleteView(
              { id: view.id },
              { onSuccess: () => setIsDeleteDialogOpen(false) }
            )
          }
        />
      </DialogContent>
    </Dialog>
  );
};
